import { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { FiCalendar, FiArchive, FiSettings, FiPlus } from "react-icons/fi"
import { TbSquareLetterP, TbSquareLetterM, TbSquareLetterV } from "react-icons/tb";


import "./index.scss"

import { Popup, ListItemDnD, ConfirmDialog } from "components"
import { MemberConfig } from "components/Config"
import { members as membersState, teamInfo, projects, limsCapabilities } from "state_management"
import dragTypes from "utils/scripts/dragTypes"
import { Button } from "react-bootstrap";
import useT from "utils/hooks/useT"

export default function TeamTab() {
  const [addConfig, setAddConfig] = useState(false)
  const [activeEdit, setActiveEdit] = useState(null)
  const [activeArchive, setActiveArchive] = useState(null)

  const members = Object.values(useSelector((state) => state.members))
  const dispatch = useDispatch()
  const { t } = useT("common")
  const hasFetched = useSelector((state) => state.teamInfo.hasFetched)
  // SPEC-057 L1 — the archive authorisation matrix (FE mirror; the backend enforces it and
  // answers 409 MEMBER_HOLDS_QA_ROLE / 403 as fallback).
  const authRole = useSelector((state) => state.auth.role)
  const authMemberId = useSelector((state) => state.auth.memberId)
  const showLims = useSelector((state) => state.teamStatus.showLims)
  const actorIsQaManager = useSelector(limsCapabilities.selectIsLimsQaManager)
  // The dispatch alone might be causing the  error 'Warning: Cannot update a component (`TeamTab`) while rendering a different component (`TeamTab`)' 
  // Put it inside useEffect to avoid rendering while in other screen
  useEffect(() => {
    if(!hasFetched) {
      dispatch(teamInfo.fetch())
      }
  })
  
  const team = useSelector((state) => state.teamInfo.teamName)

  return (
    <div className="TeamTab-main-container">
      {activeArchive && (
        <ConfirmDialog
          onCancel={() => setActiveArchive(null)}
          onConfirm={() =>
            dispatch(membersState.archive(activeArchive.id))
              .then(() => dispatch(projects.fetch()))
              // The toast (incl. the translated 409 MEMBER_HOLDS_QA_ROLE fallback) comes from
              // the message layer — just close the dialog either way.
              .catch(() => {})
              .finally(() => setActiveArchive(null))
          }
        >
          <h4>{t("confirmArchivalOfMember", { initials: activeArchive.initials })}</h4>
        </ConfirmDialog>
      )}
      {activeEdit && (
        <Popup nonScrollable onCancel={() => setActiveEdit(null)}>
          <MemberConfig member={activeEdit} onClose={() => setActiveEdit(null)} />
        </Popup>
      )}
      {addConfig && (
        <Popup nonScrollable onCancel={() => setAddConfig(false)}>
          <MemberConfig onClose={() => setAddConfig(false)} />
        </Popup>
      )}
      <div className="Setup-add-button-header">
        <div className="Setup-add-button">
          <Button onClick={() => setAddConfig(true)}>{t("addMember")}</Button>
        </div>
      </div>
      <div className="TeamName-container">
        <p>{team}</p>
      </div>
      <div className="TeamTab-member-container">
        {members
          .filter((member) => !member.archived)
          .map((member) => {
            const archiveState = limsCapabilities.archiveDecision({
              actorRole: authRole,
              actorIsQaManager,
              actorMemberId: authMemberId,
              showLims,
              target: member,
            })
            return (
            <ListItemDnD
              text={member.initials}
              key={member.id}
              dragType={dragTypes.MEMBER}
              id={member.id}
              item={member}
              onDrop={(dragId, dropId) => {
                const dropIdx = members.findIndex((x) => x.id === dropId)
                const dragIdx = members.findIndex((x) => x.id === dragId)
                if (dragIdx > dropIdx) {
                  members.splice(dropIdx, 0, members[dragIdx])
                  members.splice(dragIdx + 1, 1)
                } else {
                  members.splice(dropIdx + 1, 0, members[dragIdx])
                  members.splice(dragIdx, 1)
                }
                const ids = members.map((x) => x.id)
                dispatch(membersState.editOrder({ ids: ids }))
              }}
            >
              {member.outlookLink && (
                <FiCalendar
                  className="TeamTab-member-outlook-icon"
                  title={t("memberHasEnabledICalendarOutlookIntegration")}
                />
              )}
              {member.role === "MEMBER" ?
                <TbSquareLetterM title={t("member")}/>
                : member.role === "VIEW_ONLY" ?
                <TbSquareLetterV title={t("viewOnly")}/>
                :
                <TbSquareLetterP title={t("planner")}/>
              }
              {/* SPEC-056 UI-review: the QA qualifications are independent of the planning
                  role — show them as their own badges (LIMS teams only). */}
              {showLims && (member.qcCapabilities || []).includes("qa-manager") && (
                <span className="TeamTab-qa-badge" title={t("limsCapQaManager")}>QM</span>
              )}
              {showLims &&
                (member.qcCapabilities || []).includes("qa") &&
                !(member.qcCapabilities || []).includes("qa-manager") && (
                  <span className="TeamTab-qa-badge" title={t("limsCapQa")}>QA</span>
                )}
              {archiveState.allowed && (
                <FiArchive onClick={() => setActiveArchive(member)} title={t("archiveMember")} />
              )}
              {!archiveState.allowed && archiveState.tooltipKey && (
                <FiArchive className="TeamTab-archive-disabled" title={t(archiveState.tooltipKey)} />
              )}
              <FiSettings onClick={() => setActiveEdit(member)} title={t("editMember")} />
            </ListItemDnD>
            )
          })}
        <div className="TeamTab-add-member">
          <FiPlus title={t("addMember")} onClick={() => setAddConfig(true)} />
        </div>
      </div>
    </div>
  )
}
